import {Component, EventEmitter, Input, Output, TemplateRef, ViewChild} from '@angular/core';
import {IScrollDataInput} from '../../models';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';

/**
 * ### SharedScrollingVirtualComponent
 * A reusable virtual scroll component for efficiently rendering large lists with customizable item templates.
 *
 * @description
 * This component uses Angular CDK's Virtual Scroll to render only visible items, improving performance.
 * It accepts a scroll data input with data array and supports lazy loading by emitting an event
 * when the scroll nears the end of the data list. It also supports custom item height and template.
 *
 * The component includes two `<ng-content>` slots for projected content:
 * - `[noData]` — Displayed when there is no data to show.
 * - `[scrollLoader]` — Displayed when the data is loading.
 *
 * @example
 * <app-shared-scrolling-virtual
 *   [scrollData]="myScrollData"
 *   [itemHeight]="50"
 *   [itemTemplate]="myTemplate"
 *   (scrolledToEnd)="loadMoreItems()"
 * >
 *   <ng-template noData>
 *     <p>No items available</p>
 *   </ng-template>
 *
 *   <ng-template scrollLoader>
 *     <mat-spinner diameter="30"></mat-spinner>
 *   </ng-template>
 * </app-shared-scrolling-virtual>
 *
 * @contentChild noData - Content shown when data array is empty.
 * @contentChild scrollLoader - Content shown while data is loading.
 */
@Component({
    selector: 'app-shared-scrolling-virtual',
    templateUrl: './shared-scrolling-virtual.component.html',
    styleUrls: ['./shared-scrolling-virtual.component.scss'],
    standalone: false
})
export class SharedScrollingVirtualComponent {

  /**
   * The property name to use as the unique identifier for items.
   * @type {string}
   * @default 'id'
   * @example 'userId'
   */
  @Input() idProperty = 'id';

  /**
   * Indicates if data is currently loading and should show a loading indicator.
   * @type {boolean}
   * @default false
   */
  @Input() isScrollDataLoading = false;

  /**
   * The fixed height of each item in the list, in pixels.
   * @type {number}
   * @example 48
   */
  @Input() itemHeight!: number;

  /**
   * The template reference for rendering each individual item.
   * @type {TemplateRef<unknown>}
   */
  @Input() itemTemplate!: TemplateRef<unknown>;

  /**
   * The number of items from the end of the list at which the scrolledToEnd event is triggered.
   * Useful for implementing infinite scroll.
   * @type {number}
   * @default 10
   */
  @Input() threshold = 10;

  /**
   * The scroll data input, which contains the data array to render.
   * @type {IScrollDataInput<unknown>}
   */
  @Input() scrollData!: IScrollDataInput<unknown>;

  /**
   * Event emitted when the user scrolls near the end of the list, based on the threshold.
   * Used to trigger loading more data.
   * @event
   */
  @Output() readonly scrolledToEnd = new EventEmitter<void>();

  /**
   * Reference to the virtual scroll viewport for internal access.
   * @type {CdkVirtualScrollViewport}
   */
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  /**
   * Returns the data array to render, or an empty array if invalid.
   * @readonly
   * @type {unknown[]}
   */
  get data(): unknown[] {
    return Array.isArray(this.scrollData?.data) ? this.scrollData.data as unknown[] : [];
  }

  /**
   * Returns the length of the data array.
   * @readonly
   * @type {number}
   */
  get dataLength(): number {
    return Array.isArray(this.data) ? this.data.length : 0;
  }

  /**
   * Returns the height of each item in pixels.
   * @readonly
   * @type {number}
   */
  get itemHeightInPx(): number {
    return this.itemHeight;
  }

  /**
   * Handler for the virtual scroll's scrolledIndexChange event.
   * Emits scrolledToEnd if the scroll index is within the threshold from the end.
   *
   * @param {number} index - The current scrolled index.
   * @returns {void}
   */
  onScrolledIndexChange(index: number): void {
    const total = this.dataLength;
    if (index >= total - this.threshold) {
      this.scrolledToEnd.emit();
    }
  }

  /**
   * TrackBy function for efficient rendering of items by unique id.
   *
   * @param {number} index - The index of the item.
   * @param {unknown} item - The item object.
   * @returns {string} The unique identifier for the item.
   */
  trackById(index: number, item: unknown): string {
    return String(item?.[this.idProperty]) ?? index.toString();
  }

}
